import type { InstanceExecutiveAlert } from './operational-control/instance-executive-alerts';
export const DASHBOARD_CYCLE_YEAR = 2026;
const MONTHS = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
export type DashboardObligation = InstanceExecutiveAlert & { indicatorId: string; monthIndex: number; reason: 'MISSING_CAPTURE' | 'TARGET_SHORTFALL'; };
const number = (v: unknown) => Number.isFinite(Number(v)) ? Number(v) : 0;
export const hasDashboardCapture = (entry: any): boolean => {
  if (!entry) return false;
  if (typeof entry.captureConfirmedAt === 'string' && Number.isFinite(Date.parse(entry.captureConfirmedAt))) return true;
  if (entry.real !== null && entry.real !== undefined && String(entry.real).trim() !== '' && Number.isFinite(Number(entry.real))) return true;
  if (String(entry.note || '').trim()) return true;
  return ['checklist','elements','activities'].some(key => Array.isArray(entry[key]) && entry[key].some((item: any) =>
    item.completed === true || number(item.realizado ?? item.real ?? item.value) > 0));
};
export const dashboardCalendar = (year: number, now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {timeZone:'America/Mexico_City',year:'numeric',month:'numeric'}).formatToParts(now);
  const currentYear = Number(parts.find(p => p.type === 'year')?.value);
  const currentMonth = Number(parts.find(p => p.type === 'month')?.value) - 1;
  return { lastClosedMonth: currentYear > year ? 11 : currentYear < year ? -1 : currentMonth - 1, currentMonth: currentYear === year ? currentMonth : null };
};
// Legacy histories have no certified explicit deadline or compensation contract.
// Those policies must be supplied explicitly; they are never inferred from a later surplus.
export const evaluateDashboardObligation = (input: {
  indicator: any; entry: any; monthIndex: number; year: number; now?: Date;
  deadline?: string; compensation?: { allowed: boolean; target: number; actual: number };
}): DashboardObligation | null => {
  const {indicator, entry, monthIndex, year} = input;
  if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11 || !Number.isInteger(year) || (indicator.frec ?? indicator.frequency) !== 'Mensual' || number(entry?.meta) <= 0) return null;
  const now = input.now || new Date();
  const explicitDeadline = input.deadline && /^\d{4}-\d{2}-\d{2}T/.test(input.deadline) && Number.isFinite(Date.parse(input.deadline));
  const due = explicitDeadline ? Date.parse(input.deadline!) < now.getTime() : monthIndex <= dashboardCalendar(year, now).lastClosedMonth;
  if (!due) return null;
  const captured = hasDashboardCapture(entry);
  const target = number(entry.meta), actual = number(entry.real);
  const minimizing = indicator.tipo === 'Minimizar';
  const shortfall = minimizing ? actual > target : actual < target;
  if (captured) return null;
  if (input.compensation?.allowed && (minimizing ? input.compensation.actual <= input.compensation.target : input.compensation.actual >= input.compensation.target)) return null;
  const name = indicator.nombre || indicator.name || 'Indicador';
  const periodLabel = MONTHS[monthIndex] + ' ' + year;
  return {
    code: 'MISSING_CAPTURE:' + indicator.id + ':' + year + ':' + monthIndex,
    label: name, detail: 'Captura faltante para meta exigible ' + target + '.',
    severity: 'CRITICAL', affectsScore: false,
    periodLabel, triggerData: 'Sin evidencia de captura al cierre.',
    indicatorId: String(indicator.id), monthIndex, reason: 'MISSING_CAPTURE',
  };
};
export const buildDashboardObligations = (indicators: readonly any[], selectedMonthIndex: number, year = DASHBOARD_CYCLE_YEAR, now = new Date()): DashboardObligation[] => {
  const obligations = new Map<string, DashboardObligation>();
  for (const indicator of indicators) {
    const history = Array.isArray(indicator.history) ? indicator.history : [];
    for (let monthIndex = 0; monthIndex <= Math.min(selectedMonthIndex,11); monthIndex++) {
      const candidates = history.filter((entry: any) => String(entry.period || '').toUpperCase() === MONTHS[monthIndex]);
      const entry = candidates.find(hasDashboardCapture) || candidates[0];
      if (!entry) continue;
      const obligation = evaluateDashboardObligation({indicator,entry,monthIndex,year,now});
      if (obligation) obligations.set(obligation.code,obligation);
    }
  }
  return [...obligations.values()].sort((a,b) => a.monthIndex-b.monthIndex || a.label.localeCompare(b.label,'es'));
};
/**
 * SEG-04: single classification per indicator-month.
 * NOT_APPLICABLE: no monthly obligation (non-monthly or meta <= 0).
 * NOT_DUE: obligation exists but the period is not yet closed.
 * MISSING_CAPTURE: closed obligation without valid capture (the only operational alert).
 * TARGET_SHORTFALL: captured but below target; a result, never a capture alert.
 * MET: captured and target met.
 * @example classifyDashboardPeriod({indicator:{frec:'Mensual'},entry:{meta:15,real:14},monthIndex:8,year:2026}) // 'TARGET_SHORTFALL' once SEP is closed
 */
export type DashboardPeriodStatus = 'NOT_APPLICABLE' | 'NOT_DUE' | 'MISSING_CAPTURE' | 'TARGET_SHORTFALL' | 'MET';
export const isMonthlyObligation = (indicator: any, entry: any): boolean =>
  (indicator?.frec ?? indicator?.frequency) === 'Mensual' && number(entry?.meta) > 0;
export const classifyDashboardPeriod = (input: { indicator: any; entry: any; monthIndex: number; year: number; now?: Date }): DashboardPeriodStatus => {
  const { indicator, entry, monthIndex, year } = input;
  if (!isMonthlyObligation(indicator, entry)) return 'NOT_APPLICABLE';
  if (monthIndex > dashboardCalendar(year, input.now || new Date()).lastClosedMonth) return 'NOT_DUE';
  if (!hasDashboardCapture(entry)) return 'MISSING_CAPTURE';
  const target = number(entry.meta), actual = number(entry.real);
  const shortfall = indicator.tipo === 'Minimizar' ? actual > target : actual < target;
  return shortfall ? 'TARGET_SHORTFALL' : 'MET';
};
/** Capture counter for one month using the same obligation contract as the alerts. */
export const buildDashboardCaptureCounter = (indicators: readonly any[], monthIndex: number) => {
  let obligations = 0, captured = 0;
  for (const indicator of indicators) {
    const history = Array.isArray(indicator.history) ? indicator.history : [];
    const candidates = history.filter((entry: any) => String(entry.period || '').toUpperCase() === MONTHS[monthIndex]);
    const entry = candidates.find(hasDashboardCapture) || candidates[0];
    if (!isMonthlyObligation(indicator, entry)) continue;
    obligations++;
    if (hasDashboardCapture(entry)) captured++;
  }
  return { obligations, captured, missing: obligations - captured };
};
export type ProtocolType = 'PAI' | 'MITIGACIÓN' | 'MANTENIMIENTO';
export const protocolTypes: ProtocolType[] = ['PAI','MITIGACIÓN','MANTENIMIENTO'];
export const storedProtocolType = (indicator: any): string => String(indicator.protocolType || indicator.protocol_type || indicator.logic_config?.protocol_type || '').trim();
export const storedProtocolActions = (indicator: any): any[] => {
  const actions = indicator.protocolActions ?? indicator.protocol_actions ?? indicator.logic_config?.protocol_actions;
  return Array.isArray(actions) ? actions : [];
};
export const hasProtocolContent = (actions: readonly any[]) => actions.some(a => String(a.accion || '').trim() || String(a.notas || '').trim());
export const protocolSaveType = (indicator: any, selection: string): string | null => {
  const stored = storedProtocolType(indicator);
  if (hasProtocolContent(storedProtocolActions(indicator))) return stored || (protocolTypes.includes(selection as ProtocolType) ? selection : null);
  return protocolTypes.includes(selection as ProtocolType) ? selection : null;
};
